import React from 'react';
import MuiMenu from '@mui/material/Menu';
import { styled } from '@mui/material/styles';
import { radius } from '../theme/tokens.radius';
import { shadows as uikitShadows } from '../theme/tokens.shadows';
import uikitScrollStyle from '../theme/useUIKitScrollStyles';

import { colors } from '../theme/tokens.colors';

export interface MenuStyleProps {
  minWidth?: number;
  width?: number | string;
}

interface UIKitMenuProps {
  id?: string;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  scrollable?: boolean;
  children: React.ReactNode;
  menuStyles?: MenuStyleProps;
}

const StyledMenu = styled(MuiMenu, {
  shouldForwardProp: (prop) => prop !== 'menuStyles' && prop !== 'scrollable',
})<{ menuStyles?: MenuStyleProps; scrollable?: boolean }>(({ menuStyles, scrollable }) => ({
  '& .MuiMenu-paper': {
    minWidth: menuStyles && typeof menuStyles.minWidth === 'number' ? menuStyles.minWidth : 180,
    width: menuStyles && typeof menuStyles.width !== 'undefined' ? menuStyles.width : 'auto',
    borderRadius: radius.xs,
    backgroundColor: colors.specialColors.white,
    boxShadow: uikitShadows.small,
  },
  '& .MuiList-root': {
    paddingTop: 8,
    paddingBottom: 8,
    ...(scrollable && {
      maxHeight: 220,
      overflowY: 'auto',
      // En la versión con makeStyles había 2 definiciones de scrollbar
      // compitiendo acá (una local en este archivo con width:10/colores
      // propios, y el hook compartido useUIKitScrollStyles() con width:6) -
      // por orden de inserción de JSS, la del hook compartido siempre
      // ganaba para cada propiedad que ambas definían (misma especificidad,
      // gana el último insertado). track/thumb locales quedaban sin efecto
      // real - se usa directo uikitScrollStyle acá, mismo resultado visible.
      ...uikitScrollStyle,
    }),
  },
}));

const UIKitMenu: React.FC<UIKitMenuProps> = ({
  id,
  anchorEl,
  open,
  onClose,
  scrollable = false,
  children,
  menuStyles,
}) => (
  <StyledMenu
    id={id}
    anchorEl={anchorEl}
    keepMounted
    open={open}
    onClose={onClose}
    // anchorOrigin/transformOrigin ya alinean debajo del botón - getContentAnchorEl
    // (prop de MUI v4) fue eliminado en v5, ya no hace falta.
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    menuStyles={menuStyles}
    scrollable={scrollable}
    slotProps={{ list: { disablePadding: true } }}
  >
    {children}
  </StyledMenu>
);

export default UIKitMenu;
